import * as React from "react";
import { useState } from "react";
import { Button, Input } from "@/components/ui";
import { MfaServices } from "@/services/bedrock/MfaServices";
import { useSocialCoachSession } from "@/utils";
import { formatUsPhone } from "@/utils/mfaHelpers";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSmsSetup } from "../context";

export const VerifyPhone: React.FC = () => {
  const { phone, setStep, isLoading, setIsLoading, onSuccess, onOpenChange } = useSmsSetup();
  const { refreshSession } = useSocialCoachSession();
  const [code, setCode] = useState("");

  const handleVerifySmsCode = async () => {
    setIsLoading(true);
    try {
      const result = await MfaServices.verifyChallenge(code, "sms");
      if (result.status === "success") {
        toast.success("Phone verified successfully!");
        await refreshSession(true);
        onSuccess();
        onOpenChange(false);
      } else {
        toast.error("Invalid code");
      }
    } catch (e) {
      toast.error("Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestSmsCode = async () => {
    setIsLoading(true);
    try {
      const cleaned = phone.replace(/\D/g, "");
      const formattedPhone = phone.startsWith("+1") ? phone : `+1${cleaned}`;
      await MfaServices.updateMfaPhone(formattedPhone);
      await MfaServices.acceptSmsTos();
      await MfaServices.requestChallenge("sms");
      toast.success("Verification code resent!");
    } catch (e) {
      toast.error("Failed to resend code.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData) {
      setCode(pastedData);
      const focusIndex = Math.min(pastedData.length, 5);
      document.getElementById(`sms-code-${focusIndex}`)?.focus();
    }
  };

  const handleGoBack = () => {
    setStep("ENTER_PHONE");
    setCode("");
  };

  return (
    <div className="flex flex-col items-center justify-center py-6 space-y-6 flex-1 h-full max-w-md mx-auto text-center animate-in fade-in duration-200">
      <div className="w-full space-y-6">
        <div className="space-y-2">
          <div className="text-xl font-bold text-slate-800">Enter verification code</div>
          <p className="text-sm text-slate-500">
            We've texted a 6-digit verification code to{" "}
            <span className="text-slate-800 font-bold">{formatUsPhone(phone)}</span>. Please enter it below.
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex justify-center gap-2.5">
            {[...Array(6)].map((_, i) => (
              <Input
                key={i}
                id={`sms-code-${i}`}
                className="w-12 h-14 text-center text-xl font-bold border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 shadow-sm rounded-xl p-0"
                value={code[i] || ""}
                onPaste={handlePaste}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, "").slice(-1);
                  const newCode = code.split("");
                  newCode[i] = val;
                  const joined = newCode.join("");
                  setCode(joined);
                  if (val && i < 5) {
                    document.getElementById(`sms-code-${i + 1}`)?.focus();
                  }
                }}
                onKeyDown={e => {
                  if (e.key === "Backspace" && !code[i] && i > 0) {
                    document.getElementById(`sms-code-${i - 1}`)?.focus();
                  } else if (e.key === "Enter" && code.length === 6 && !isLoading) {
                    e.preventDefault();
                    handleVerifySmsCode();
                  }
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus={i === 0}
              />
            ))}
          </div>

          <div className="text-xs text-slate-400">
            Didn't receive a code?{" "}
            <button
              type="button"
              className="text-blue-600 hover:underline font-semibold"
              onClick={handleRequestSmsCode}
              disabled={isLoading}
            >
              Resend Code
            </button>
          </div>
        </div>
      </div>

      <div className="w-full space-y-3">
        <Button
          className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl shadow-md gap-2 hover:translate-y-[-1px] active:translate-y-[1px] transition-all disabled:opacity-50"
          onClick={handleVerifySmsCode}
          disabled={isLoading || code.length < 6}
        >
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify & Enable SMS"}
        </Button>

        <button
          type="button"
          className="text-sm font-semibold text-slate-400 hover:text-slate-600 transition-colors block mx-auto py-1"
          onClick={handleGoBack}
          disabled={isLoading}
        >
          Change Phone Number
        </button>
      </div>
    </div>
  );
};
